import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  normalizeItems,
  normalizeTotalCount,
  useAddProductRagPipelineMappingMutation,
  useDeleteProductRagPipelineMappingMutation,
  useFetchProductRagPipelineMappings,
  useUpdateProductRagPipelineMappingMutation,
} from '../../../../hooks/rag-hooks';

import ProductMappingEditorDialog from './ProductMappingEditorDialog';
import { productMappingColumns } from './ragAdminUtils';

const ProductMappingsPanel: React.FC = () => {
  const [filter, setFilter] = React.useState({
    productId: '',
    purposeId: '',
    pipelineGroupId: '',
  });

  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(25);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<any | null>(null);

  const params = React.useMemo(() => {
    const out: Record<string, any> = {
      page: page + 1,
      size,
      detailed: true,
      depth: 2,
      'order-by': 'priority',
    };

    if (filter.productId.trim()) out.product = filter.productId.trim();
    if (filter.purposeId.trim()) out.purpose = filter.purposeId.trim();
    if (filter.pipelineGroupId.trim()) out.pipeline_group = filter.pipelineGroupId.trim();

    return out;
  }, [filter, page, size]);

  const query = useFetchProductRagPipelineMappings(params, {
    placeholderData: keepPreviousData,
  });

  const rows = normalizeItems<any>(query.data);
  const total = normalizeTotalCount<any>(query.data, rows);

  const addMutation = useAddProductRagPipelineMappingMutation();
  const updateMutation = useUpdateProductRagPipelineMappingMutation();
  const deleteMutation = useDeleteProductRagPipelineMappingMutation();

  const saving = addMutation.isPending || updateMutation.isPending;

  const refresh = async () => {
    await query.refetch();
  };

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setEditorOpen(true);
  };

  const save = async (payload: any) => {
    if (payload.id) {
      await updateMutation.mutateAsync(payload);
    } else {
      await addMutation.mutateAsync(payload);
    }

    setEditorOpen(false);
    setEditing(null);
    await refresh();
  };

  const remove = async (row: any) => {
    if (!row?.id) return;

    const label = row.product_name || row.product?.name || `mapping ${row.id}`;
    if (!window.confirm(`Delete RAG mapping for ${label}?`)) return;

    await deleteMutation.mutateAsync(row.id);
    await refresh();
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Product RAG Pipeline Mappings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Map actual Cisco products to RAG pipeline groups. GTM routing is derived from related products.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={refresh}>
              Refresh
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
              Add Mapping
            </Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={2}>
          <TextField
            label="Product ID"
            size="small"
            value={filter.productId}
            onChange={event => {
              setPage(0);
              setFilter(prev => ({ ...prev, productId: event.target.value }));
            }}
          />
          <TextField
            label="Purpose ID"
            size="small"
            value={filter.purposeId}
            onChange={event => {
              setPage(0);
              setFilter(prev => ({ ...prev, purposeId: event.target.value }));
            }}
          />
          <TextField
            label="Pipeline Group ID"
            size="small"
            value={filter.pipelineGroupId}
            onChange={event => {
              setPage(0);
              setFilter(prev => ({ ...prev, pipelineGroupId: event.target.value }));
            }}
          />
        </Stack>
      </Paper>

      {query.error ? (
        <Alert severity="error">
          {(query.error as Error)?.message || 'Failed to load product RAG mappings.'}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {query.isLoading || query.isFetching ? <LinearProgress sx={{ mb: 2 }} /> : null}

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                {productMappingColumns.map(col => (
                  <TableCell key={col.key} sx={{ width: col.width }}>
                    {col.label}
                  </TableCell>
                ))}
                <TableCell width={120} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id} hover>
                  {productMappingColumns.map(col => (
                    <TableCell key={col.key}>
                      {col.render ? col.render(row) : String(row[col.key] ?? '-')}
                    </TableCell>
                  ))}

                  <TableCell align="right">
                    <Tooltip title="Edit mapping">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete mapping">
                      <IconButton size="small" color="error" onClick={() => remove(row)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {!rows.length && !query.isLoading ? (
          <Typography variant="body2" color="text.secondary" mt={2}>
            No product RAG mappings found.
          </Typography>
        ) : null}

        <TablePagination
          component="div"
          count={Number(total || 0)}
          page={page}
          rowsPerPage={size}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={event => {
            setSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <ProductMappingEditorDialog
        open={editorOpen}
        initialValue={editing}
        saving={saving}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSubmit={save}
      />
    </Stack>
  );
};

export default ProductMappingsPanel;